import "./sentry";
// Self-hosted fonts (bundled woff2) — replaces the render-blocking
// fonts.googleapis.com stylesheet that sat on the critical path (LCP, fb#156).
import "@fontsource/black-ops-one/400.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/700.css";
import "@fontsource/jetbrains-mono/400.css";
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router";
import { Alert, Box, Button, CssBaseline, ThemeProvider } from "@mui/material";
import * as Sentry from "@sentry/react";
import App from "./App.jsx";
import { AuthProvider } from "src/context/AuthContext";
import { LanguageProvider } from "src/i18n";
import { resolveLanguage, STORAGE_KEY } from "src/i18n/resolveLanguage";
import { installDomPatches } from "src/utils/domPatches";
import { theme } from "src/theme/themes";
import { initAnalytics } from "src/analytics/gtag";
import { captureAttribution, attachGaClientId } from "src/utils/attribution";

// Guard React's DOM ops against browser page translators before anything
// renders (Google Translate mangles React-managed text nodes — see domPatches.js).
installDomPatches();

// Suppress Chrome's automatic "Asenna sovellus" install banner on mobile.
// The manifest must stay installable (display: standalone) because iOS web
// push requires Add-to-Home-Screen; install remains available via browser menu.
window.addEventListener("beforeinstallprompt", (e) => e.preventDefault());

// Set Consent Mode v2 default (denied) and load gtag.js before React renders.
// No-op unless VITE_GA4_ID / VITE_GADS_ID are configured.
initAnalytics();
captureAttribution();   // record ad/source signals from the landing URL (best-effort)
attachGaClientId();     // best-effort GA4 client_id once gtag.js resolves

// Sentry.ErrorBoundary wraps LanguageProvider, so this fallback renders OUTSIDE
// the provider — useT() would throw here. Resolve the language the same
// pure way LanguageProvider itself does (resolveLanguage + its storage key)
// instead of pulling in a hook or the message bundles.
const ERROR_FALLBACK_TEXT = {
    fi: { message: "Sivun lataaminen epäonnistui. Yritä uudelleen.", retry: "Yritä uudelleen" },
    en: { message: "Failed to load the page. Please try again.", retry: "Try again" },
};

const errorBoundaryFallback = ({ resetError }) => {
    let stored;
    try { stored = window.localStorage.getItem(STORAGE_KEY); } catch { stored = null; }
    const copy = ERROR_FALLBACK_TEXT[resolveLanguage({ search: window.location.search, stored })];
    return (
        <Box sx={{ p: 3, maxWidth: 480, mx: "auto" }}>
            <Alert severity="error" sx={{ mb: 2 }}>
                {copy.message}
            </Alert>
            <Button variant="contained" onClick={resetError}>{copy.retry}</Button>
        </Box>
    );
};

ReactDOM.createRoot(document.getElementById("root")).render(
    <React.StrictMode>
        <Sentry.ErrorBoundary fallback={errorBoundaryFallback} showDialog={false}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <LanguageProvider>
                    <BrowserRouter>
                        <AuthProvider>
                            <App />
                        </AuthProvider>
                    </BrowserRouter>
                </LanguageProvider>
            </ThemeProvider>
        </Sentry.ErrorBoundary>
    </React.StrictMode>,
);
